// "What's an AI harness?" — concept primer with 4 shape cards (Agent / Tool /
// Branch / Human). Hue mapping uses the node-* token families per design
// system §02 (only place outside the canvas where these hues are allowed).

const ITEMS: Array<{
  hue: number;
  label: string;
  shape: "rect" | "diamond" | "hex" | "pill";
  desc: string;
}> = [
  {
    hue: 268,
    label: "Agent",
    shape: "rect",
    desc: "An LLM call with a role and prompt. The thinking parts.",
  },
  {
    hue: 175,
    label: "Tool",
    shape: "hex",
    desc: "A deterministic function. Sandboxes, repos, APIs. The hands.",
  },
  {
    hue: 35,
    label: "Branch",
    shape: "diamond",
    desc: "If / loop / parallel. The control flow that makes it a system.",
  },
  {
    hue: 340,
    label: "Human",
    shape: "pill",
    desc: "A gate where a person reviews or approves. The trust layer.",
  },
];

export default function HomeConcept() {
  return (
    <section
      id="concept"
      className="mx-auto max-w-[1320px] border-t border-border px-7 py-16"
    >
      <div className="grid grid-cols-[1fr_1.6fr] gap-16">
        <div>
          <div className="mb-3.5 font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-muted">
            01 — Concept
          </div>
          <h2 className="serif mb-[18px] text-[52px] font-normal leading-none tracking-[-0.02em] text-ink">
            What&rsquo;s an
            <br />
            AI harness?
          </h2>
          <p
            className="m-0 text-[15px] leading-[1.55] text-ink-2"
            style={{ textWrap: "pretty" }}
          >
            The orchestrator code, prompts, tool wiring, and control flow that
            turn one or more LLMs into a system that does useful work. One LLM
            call is a feature. A harness is a product.
          </p>
          <p
            className="mt-3.5 text-[13.5px] leading-[1.55] text-muted"
            style={{ textWrap: "pretty" }}
          >
            Most teams ship harnesses today as 800-line Python files nobody else
            can read. Harness Builder makes them legible &mdash; a graph anyone
            on the team can reason about, edit, and trust.
          </p>
        </div>

        <div className="grid grid-cols-2 gap-3.5">
          {ITEMS.map((it) => (
            <div
              key={it.label}
              className="flex min-h-[144px] flex-col gap-3 rounded-[14px] border border-border bg-surface p-[18px]"
            >
              <ConceptShape hue={it.hue} shape={it.shape} />
              <div>
                <div
                  className="font-mono text-[10px] font-semibold uppercase tracking-[0.10em]"
                  style={{ color: `oklch(0.32 0.10 ${it.hue})` }}
                >
                  {it.label}
                </div>
                <div className="mt-1 text-[13.5px] leading-[1.45] text-ink-2">
                  {it.desc}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ConceptShape({
  hue,
  shape,
}: {
  hue: number;
  shape: "rect" | "diamond" | "hex" | "pill";
}) {
  const w = 56;
  const h = 36;
  const fill = `oklch(0.96 0.04 ${hue})`;
  const stroke = `oklch(0.62 0.16 ${hue})`;
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} aria-hidden>
      {shape === "rect" && (
        <rect
          x="1"
          y="1"
          width={w - 2}
          height={h - 2}
          rx="6"
          fill={fill}
          stroke={stroke}
          strokeWidth="1.4"
        />
      )}
      {shape === "diamond" && (
        <polygon
          points={`${w / 2},2 ${w - 2},${h / 2} ${w / 2},${h - 2} 2,${h / 2}`}
          fill={fill}
          stroke={stroke}
          strokeWidth="1.4"
        />
      )}
      {shape === "hex" && (
        <polygon
          points={`8,2 ${w - 8},2 ${w - 2},${h / 2} ${w - 8},${h - 2} 8,${h - 2} 2,${h / 2}`}
          fill={fill}
          stroke={stroke}
          strokeWidth="1.4"
        />
      )}
      {shape === "pill" && (
        <rect
          x="1"
          y="1"
          width={w - 2}
          height={h - 2}
          rx={h / 2 - 1}
          fill={fill}
          stroke={stroke}
          strokeWidth="1.4"
        />
      )}
    </svg>
  );
}
